import { useNavigate, useLocation } from "react-router-dom";
import { Home, Compass, Bot, Swords, User, Bell, Sun, Moon } from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";

const navItems = [
  { icon: Home, label: "Home", path: "/" },
  { icon: Compass, label: "Explore", path: "/community" },
  { icon: Bot, label: "Pilot", path: "/ceo-pilot" },
  { icon: Swords, label: "Debate", path: "/arena" },
  { icon: User, label: "Profile", path: "/profile" },
  { icon: Bell, label: "Alerts", path: "/dashboard", hasBadge: true },
];

const HIDDEN_ROUTES = ["/auth", "/reset-password", "/checkout/return"];
const HIDDEN_PREFIXES = ["/legal"];

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [unreadCount, setUnreadCount] = useState(3); // seed count

  useEffect(() => {
    if (!user) return;
    // Listen for new notifications via realtime
    const channel = supabase
      .channel("bottom-nav-alerts")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "post_replies" }, () => {
        setUnreadCount(prev => prev + 1);
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "post_reposts" }, () => {
        setUnreadCount(prev => prev + 1);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  // Clear badge when navigating to alerts
  useEffect(() => {
    if (location.pathname === "/dashboard") {
      setUnreadCount(0);
    }
  }, [location.pathname]);

  if (HIDDEN_ROUTES.includes(location.pathname) || HIDDEN_PREFIXES.some(p => location.pathname.startsWith(p))) return null;

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card/95 backdrop-blur-md md:hidden">
      <div className="flex items-center justify-around h-14 max-w-2xl mx-auto px-2">
        {navItems.map(item => {
          const isActive = location.pathname === item.path ||
            (item.path !== "/" && location.pathname.startsWith(item.path.split("?")[0]));
          return (
            <motion.button
              key={item.label}
              onClick={() => navigate(item.path)}
              whileTap={{ scale: 0.85 }}
              className={`relative flex flex-col items-center gap-0.5 text-[9px] font-medium transition-all duration-200 ${isActive ? "text-sovereign-gold" : "text-muted-foreground hover:text-foreground"}`}
            >
              <motion.div
                animate={isActive ? { y: -2 } : { y: 0 }}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
              >
                <item.icon className="w-4.5 h-4.5" />
              </motion.div>
              {/* Notification badge */}
              {item.hasBadge && unreadCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 min-w-[14px] h-[14px] rounded-full bg-sovereign-gold text-primary-foreground text-[8px] font-bold flex items-center justify-center px-0.5"
                >
                  {unreadCount > 9 ? "9+" : unreadCount}
                </motion.span>
              )}
              {/* Active indicator dot */}
              {isActive && (
                <motion.div
                  layoutId="bottomNavIndicator"
                  className="absolute -bottom-1 w-1 h-1 rounded-full bg-sovereign-gold"
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
              <span className="transition-colors duration-200">{item.label}</span>
            </motion.button>
          );
        })}
        {/* Theme toggle */}
        <motion.button
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          whileTap={{ scale: 0.85 }}
          className="relative flex flex-col items-center gap-0.5 text-[9px] font-medium text-muted-foreground hover:text-foreground transition-all duration-200"
        >
          <motion.div
            key={theme}
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
          >
            {theme === "dark" ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />}
          </motion.div>
          <span>{theme === "dark" ? "Light" : "Dark"}</span>
        </motion.button>
      </div>
    </nav>
  );
};

export default BottomNav;
